import { z } from 'zod';
import {
  VerifiableDocumentFragment,
  VerifiableDocumentType,
} from '@src/generated/graphql';
import { extractFileInputFromFragment, fileSchema } from './file';

const ONE_VERIFIABLE_DOCUMENT_TYPES = [
  'PASSPORT',
  'BIRTH_CERTIFICATE',
  'PORTUGUESE_BIRTH_CERTIFICATE',
  'BANK_STATEMENT',
  'UTILITY_BILL',
  'ADDITIONAL_DOCUMENT',
  'PROOF_OF_EMPLOYMENT',
  'FISCAL_REP_CANCELLATION_REQUEST',
] as const;

const TWO_VERIFIABLE_DOCUMENT_TYPES = ['EU_ID', 'DRIVER_LICENSE'] as const;

// In the future we can use https://github.com/colinhacks/zod/pull/1213 for stronger type support

export const verifiableDocumentSchema = z
  .object(
    {
      id: z.string().optional(),
      type: z.enum([
        ...ONE_VERIFIABLE_DOCUMENT_TYPES,
        ...TWO_VERIFIABLE_DOCUMENT_TYPES,
      ]),
      fileOne: fileSchema,
      fileTwo: fileSchema.nullish(),
    },
    {
      invalid_type_error: 'translation:validation.file_required',
    },
  )
  .superRefine((val, ctx) => {
    if (
      TWO_VERIFIABLE_DOCUMENT_TYPES.includes(
        val.type as (typeof TWO_VERIFIABLE_DOCUMENT_TYPES)[number],
      ) &&
      !val.fileTwo
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fileTwo'],
        message: 'translation:validation.file_required',
      });
    }
  });

export type VerifiableDocumentInput = z.infer<typeof verifiableDocumentSchema>;

export function extractVerifiableDocumentInputFromFragment(
  document: VerifiableDocumentFragment | undefined | null,
  defaultType?: VerifiableDocumentType,
): Partial<VerifiableDocumentInput> {
  if (!document) {
    return { type: defaultType };
  }
  if (document.status === 'REJECTED') {
    return { type: document.type };
  }
  return {
    id: document.id,
    type: document.type,
    fileOne: extractFileInputFromFragment(document.fileOne),
    fileTwo: document.fileTwo && extractFileInputFromFragment(document.fileTwo),
  };
}
